import { TabsWithRoutes } from "@components/common/tabs/tabs-with-routes";
import { AnalyticsProvideFeedbackButton } from "@components/analytics/in-house/feedback/analytics-provide-feedback-button";
import { useAnalytics } from "@hooks/analytics/use-analytics";
import { GroupsAnalytics } from "@pages/analytics/groups-analytics";
import { InHouseAnalyticsPage } from "@pages/analytics/in-house-analytics-page";
import { MembersAnalytics } from "@pages/analytics/members-analytics";
import { ProjectsAnalytics } from "@pages/analytics/projects-analytics";
import { WorkspaceAnalytics } from "@pages/analytics/workspace-analytics";
import { NotFoundPage } from "@pages/not-found-page";
import { AnalyticsTabs } from "@router/route-params";
import { useAppParams } from "@router/router-helper";
import { runtimeConfig } from "@src/runtime-config";
import { appStateSelector } from "@store/app/app-selector";
import { useAppSelector } from "@store/store-helper";
import { RequiredFeatureCompanyLevelName } from "@utils/feature-control/company/company-feature-control-types";
import { OpenTabEvents } from "@utils/track-event/track-event-list";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";

/** Whether premium analytics are not available for the current environment */
const isPremiumAnalyticsNotAvailable =
  runtimeConfig.appEnv === "eu-dev" ||
  runtimeConfig.appEnv === "com-staging" ||
  runtimeConfig.appEnv === "eu-staging";

/** Renders the contents in analytics page */
export function AnalyticsPage(): JSX.Element {
  const { analyticsTabs } = useAppParams();
  useAnalytics();

  const { isAlphaTestingEnabled } = useAppSelector(appStateSelector);
  const { canViewPremiumAnalytics } = useHasUserValidFeatureCompanyLevel();
  const isPremiumAnalyticsHidden =
    isPremiumAnalyticsNotAvailable ||
    !canViewPremiumAnalytics ||
    isAlphaTestingEnabled;

  if (
    analyticsTabs === undefined ||
    !Object.values(AnalyticsTabs).includes(analyticsTabs)
  ) {
    return <NotFoundPage />;
  }

  return (
    <TabsWithRoutes
      selectedTab={analyticsTabs}
      dataTestId="ANALYTICS_CONTAINER"
      requiredAttribute={false}
      trackingEventPageName={OpenTabEvents.openAnalyticsTab}
      tabs={[
        {
          label: "Workspace",
          route: AnalyticsTabs.workspace,
          content: () => <WorkspaceAnalytics />,
          loadingContent: <WorkspaceAnalytics />,
          requiredFeatureCompanyLevel:
            RequiredFeatureCompanyLevelName.canViewAnalytics,
        },
        {
          label: "Projects",
          route: AnalyticsTabs.projects,
          content: () => <ProjectsAnalytics />,
          loadingContent: <ProjectsAnalytics />,
          requiredFeatureCompanyLevel:
            RequiredFeatureCompanyLevelName.canViewPremiumAnalytics,
          isHidden: isPremiumAnalyticsHidden,
          actionButtons: <AnalyticsProvideFeedbackButton />,
        },
        {
          label: "Members",
          route: AnalyticsTabs.members,
          content: () => <MembersAnalytics />,
          loadingContent: <MembersAnalytics />,
          requiredFeatureCompanyLevel:
            RequiredFeatureCompanyLevelName.canViewPremiumAnalytics,
          isHidden: isPremiumAnalyticsHidden,
          actionButtons: <AnalyticsProvideFeedbackButton />,
        },
        {
          label: "Groups",
          route: AnalyticsTabs.groups,
          content: () => <GroupsAnalytics />,
          loadingContent: <GroupsAnalytics />,
          requiredFeatureCompanyLevel:
            RequiredFeatureCompanyLevelName.canViewPremiumAnalytics,
          isHidden: isPremiumAnalyticsHidden,
          actionButtons: <AnalyticsProvideFeedbackButton />,
        },
        {
          label: "In-House Analytics",
          route: AnalyticsTabs.inHouseAnalytics,
          content: () => <InHouseAnalyticsPage />,
          loadingContent: <InHouseAnalyticsPage />,
          requiredFeatureCompanyLevel:
            RequiredFeatureCompanyLevelName.canViewPremiumAnalytics,
          isHidden: !isAlphaTestingEnabled,
        },
      ]}
    />
  );
}
